<script setup>
import { computed } from 'vue';
import { useRoadmapsStore } from '@/stores/roadmaps';
import { renderRoadmapSvg, svgToPngBlob } from '@/lib/render';
import { roadmapToCsv } from '@/lib/csv';
import { downloadBlob, downloadText, roadmapFilename } from '@/lib/files';
import { toast } from '@/composables/useDialogs';
import Icon from '@/components/ui/Icon.vue';

// The same renderer the app's Export menu uses, drawing the demo roadmap as it stands now.
const store = useRoadmapsStore();
const roadmap = computed(() => store.byId('demo-q3-platform') || store.sorted[0]);
const plain = () => JSON.parse(JSON.stringify(roadmap.value));

const picture = computed(() => {
  store.revision;
  return roadmap.value ? renderRoadmapSvg(plain()) : null;
});

async function png() {
  try {
    const { svg, width, height } = picture.value;
    downloadBlob(roadmapFilename(roadmap.value, 'png'), await svgToPngBlob(svg, width, height, 2));
  } catch {
    toast('This browser could not make an image', { tone: 'error' });
  }
}

function svg() {
  downloadText(roadmapFilename(roadmap.value, 'svg'), picture.value.svg, 'image/svg+xml');
}

function csv() {
  downloadText(roadmapFilename(roadmap.value, 'csv'), `﻿${roadmapToCsv(plain())}`, 'text/csv');
}
</script>

<template>
  <div class="card">
    <div v-if="picture" class="thumb" aria-label="Preview of the exported image" role="img" v-html="picture.svg" />
    <div class="actions">
      <button type="button" class="btn btn--dark" @click="png"><Icon name="image" :size="14" />Download PNG</button>
      <button type="button" class="btn" @click="svg"><Icon name="image" :size="14" />SVG</button>
      <button type="button" class="btn" @click="csv"><Icon name="table" :size="14" />CSV</button>
    </div>
    <p class="meta">Drawn in your browser from the demo above, including your changes. The app adds print and PDF.</p>
  </div>
</template>

<style scoped lang="scss">
.card {
  padding: 22px;
  border: 1px solid var(--line);
  border-radius: var(--radius-l);
  background: var(--surface);
  box-shadow: 0 24px 48px -28px rgba(23, 20, 42, 0.25);
}

.thumb {
  overflow: hidden;
  border: 1px solid var(--line);
  border-radius: var(--radius);
  background: #fff;

  :deep(svg) {
    display: block;
    width: 100%;
    height: auto;
  }
}

.actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 36px;
  padding: 0 14px;
  border: 1px solid var(--line-strong);
  border-radius: 9px;
  background: var(--surface);
  color: var(--text);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    border-color: var(--text-3);
  }

  &--dark {
    border-color: var(--ink);
    background: var(--ink);
    color: #fff;

    &:hover {
      background: var(--ink-3);
    }
  }
}

.meta {
  margin-top: 12px;
  font-size: 12px;
  color: var(--text-3);
}
</style>
